import { useEffect, useRef, useState } from 'react'
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion'

interface AnimatedSectionProps {
  children: React.ReactNode
  animation?: 'fade-up' | 'fade-right' | 'scale-in'
  delay?: number // Stagger delay in ms
  className?: string
}

/**
 * Wrapper component that applies scroll-triggered animations to child content
 * Respects user's prefers-reduced-motion preference
 * Uses GPU acceleration and manages will-change for optimal performance
 */
export function AnimatedSection({ 
  children, 
  animation = 'fade-up', 
  delay = 0,
  className = ''
}: AnimatedSectionProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const prefersReducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsVisible(true)
      return
    }

    if (!('IntersectionObserver' in window)) {
      console.warn('Intersection Observer not supported, displaying all content immediately')
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsAnimating(true)
          setTimeout(() => {
            setIsVisible(true)
            // Remove will-change after animation completes
            setTimeout(() => setIsAnimating(false), 600)
          }, delay)
          // Disconnect observer after animation triggers for performance
          observer.disconnect()
        }
      },
      { 
        threshold: 0.1, 
        rootMargin: '0px 0px -100px 0px' // Trigger slightly before element enters viewport
      }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [delay, prefersReducedMotion])

  const animationClass = !prefersReducedMotion && isVisible
    ? animation === 'fade-up' 
      ? 'animate-fade-in-up' 
      : animation === 'fade-right' 
      ? 'animate-fade-in-right' 
      : 'animate-scale-in'
    : ''

  return (
    <div
      ref={ref}
      className={`transition-opacity duration-600 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${animationClass} ${isAnimating ? 'animating' : 'animation-complete'} ${className}`}
      style={{ 
        animationDelay: `${delay}ms`,
        transform: 'translateZ(0)' // GPU acceleration
      }}
    >
      {children}
    </div>
  )
}
